import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Page,
  Card,
  DataTable,
  Badge,
  Button,
  Filters,
  ChoiceList,
  TextField,
  Select,
  InlineStack,
  Pagination,
  BlockStack,
  SkeletonBodyText,
  EmptyState,
  Box,
  Modal,
  Tabs,
} from '@shopify/polaris';
import { assetsApi, productsApi } from '@/services';
import type { AssetStatus, InventorySummary } from '@/types';

export default function AssetsList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedTab, setSelectedTab] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newAsset, setNewAsset] = useState({
    serialNumber: '',
    productSku: '',
    productName: '',
    notes: '',
  });
  const limit = 20;
  const [selectedProductId, setSelectedProductId] = useState('');

  const { data: productsRaw } = useQuery({
    queryKey: ['products-all'],
    queryFn: () => productsApi.list(),
  });

  const rentalProducts = useMemo(
    () =>
      (productsRaw ?? []).filter(
        (p) => p.status === 'ACTIVE' && p.rentalConfig.isRentalProduct,
      ),
    [productsRaw],
  );

  const productOptions = useMemo(
    () => [
      { label: '— Select a product —', value: '' },
      ...rentalProducts.map((p) => ({ label: p.title, value: p.shopifyProductId })),
    ],
    [rentalProducts],
  );

  const handleProductSelect = useCallback(
    (productId: string) => {
      setSelectedProductId(productId);
      if (!productId) {
        setNewAsset((prev) => ({ ...prev, productName: '', productSku: '' }));
        return;
      }
      const product = rentalProducts.find((p) => p.shopifyProductId === productId);
      if (product) {
        const sku = product.variants[0]?.sku ?? '';
        setNewAsset((prev) => ({ ...prev, productName: product.title, productSku: sku }));
      }
    },
    [rentalProducts],
  );

  const { data: assets, isLoading: assetsLoading } = useQuery({
    queryKey: ['assets', page, status, searchQuery],
    queryFn: () =>
      assetsApi.list({
        page,
        limit,
        status: status[0] as AssetStatus | undefined,
        search: searchQuery || undefined,
      }),
    enabled: selectedTab === 0,
  });

  const { data: inventory, isLoading: inventoryLoading } = useQuery({
    queryKey: ['inventory-summary'],
    queryFn: () => assetsApi.getInventorySummary(),
    enabled: selectedTab === 1,
  });

  const createMutation = useMutation({
    mutationFn: (data: typeof newAsset) =>
      assetsApi.create({
        serialNumber: data.serialNumber,
        productSku: data.productSku,
        productName: data.productName,
        notes: data.notes || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-summary'] });
      setShowCreateModal(false);
      setNewAsset({ serialNumber: '', productSku: '', productName: '', notes: '' });
      setSelectedProductId('');
    },
  });

  const handleStatusChange = useCallback((value: string[]) => {
    setStatus(value);
    setPage(1);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    setPage(1);
  }, []);

  const handleClearFilters = useCallback(() => {
    setStatus([]);
    setSearchQuery('');
  }, []);

  const handleCreateAsset = useCallback(() => {
    if (!newAsset.serialNumber || !newAsset.productSku || !newAsset.productName) return;
    createMutation.mutate(newAsset);
  }, [newAsset, createMutation]);

  const getStatusBadge = (assetStatus: AssetStatus) => {
    const statusConfig: Record<AssetStatus, { tone: 'success' | 'warning' | 'critical' | 'info' | undefined; label: string }> = {
      ready: { tone: 'success', label: 'Ready' },
      allocated: { tone: 'info', label: 'Allocated' },
      in_use: { tone: undefined, label: 'In Use' },
      qc_hold: { tone: 'warning', label: 'QC Hold' },
      repair: { tone: 'critical', label: 'Repair' },
      repair_complete: { tone: 'info', label: 'Repair Complete' },
      at_clinic: { tone: undefined, label: 'At Clinic' },
      retired: { tone: 'critical', label: 'Retired' },
    };
    const config = statusConfig[assetStatus] || { tone: undefined, label: assetStatus };
    return <Badge tone={config.tone}>{config.label}</Badge>;
  };

  const assetRows = (assets?.data || []).map((asset) => [
    <Button key={asset.id} variant="plain" onClick={() => navigate(`/assets/${asset.id}`)}>
      {asset.serialNumber}
    </Button>,
    asset.gpsId ?? '—',
    asset.productSku,
    asset.productName,
    getStatusBadge(asset.assetStatus),
    asset.currentRentalId ? 'Yes' : 'No',
    new Date(asset.updatedAt).toLocaleDateString(),
  ]);

  const inventoryRows = (inventory || []).map((item: InventorySummary) => [
    item.sku,
    item.productName,
    String(item.ready),
    String(item.allocated),
    String(item.inUse),
    String(item.qcHold + item.repair),
    String(item.total),
  ]);

  const filters = [
    {
      key: 'status',
      label: 'Status',
      filter: (
        <ChoiceList
          title="Status"
          titleHidden
          choices={[
            { label: 'Ready', value: 'ready' },
            { label: 'Allocated', value: 'allocated' },
            { label: 'In Use', value: 'in_use' },
            { label: 'QC Hold', value: 'qc_hold' },
            { label: 'Repair', value: 'repair' },
            { label: 'Retired', value: 'retired' },
          ]}
          selected={status}
          onChange={handleStatusChange}
        />
      ),
      shortcut: true,
    },
  ];

  const appliedFilters = status.length > 0
    ? [{ key: 'status', label: `Status: ${status.join(', ')}`, onRemove: () => setStatus([]) }]
    : [];

  const tabs = [
    { id: 'assets', content: 'All Assets', accessibilityLabel: 'All assets' },
    { id: 'inventory', content: 'Inventory Summary', accessibilityLabel: 'Inventory summary' },
  ];

  return (
    <Page
      title="Assets"
      primaryAction={{
        content: 'Add Asset',
        onAction: () => setShowCreateModal(true),
      }}
    >
      <Card padding="0">
        <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab} />

        {selectedTab === 0 && (
          <>
            <Box padding="400">
              <Filters
                queryValue={searchQuery}
                queryPlaceholder="Search by serial or SKU..."
                filters={filters}
                appliedFilters={appliedFilters}
                onQueryChange={handleSearchChange}
                onQueryClear={() => setSearchQuery('')}
                onClearAll={handleClearFilters}
              />
            </Box>

            {assetsLoading ? (
              <Box padding="400">
                <SkeletonBodyText lines={10} />
              </Box>
            ) : assetRows.length === 0 ? (
              <Box padding="800">
                <EmptyState
                  heading="No assets found"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                  action={{ content: 'Add Asset', onAction: () => setShowCreateModal(true) }}
                >
                  <p>Add your first asset to get started.</p>
                </EmptyState>
              </Box>
            ) : (
              <>
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text']}
                  headings={['Serial', 'GPS ID', 'SKU', 'Product', 'Status', 'In Rental', 'Last Updated']}
                  rows={assetRows}
                />
                {assets && assets.meta.totalPages > 1 && (
                  <Box padding="400">
                    <InlineStack align="center">
                      <Pagination
                        hasPrevious={page > 1}
                        hasNext={page < assets.meta.totalPages}
                        onPrevious={() => setPage((p) => p - 1)}
                        onNext={() => setPage((p) => p + 1)}
                      />
                    </InlineStack>
                  </Box>
                )}
              </>
            )}
          </>
        )}

        {selectedTab === 1 && (
          <>
            {inventoryLoading ? (
              <Box padding="400">
                <SkeletonBodyText lines={10} />
              </Box>
            ) : inventoryRows.length === 0 ? (
              <Box padding="800">
                <EmptyState
                  heading="No inventory data"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <p>Add assets to see inventory summary.</p>
                </EmptyState>
              </Box>
            ) : (
              <DataTable
                columnContentTypes={['text', 'text', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric']}
                headings={['SKU', 'Product', 'Ready', 'Allocated', 'In Use', 'QC/Repair', 'Total']}
                rows={inventoryRows}
                totals={['', '',
                  String((inventory || []).reduce((sum: number, i: InventorySummary) => sum + i.ready, 0)),
                  String((inventory || []).reduce((sum: number, i: InventorySummary) => sum + i.allocated, 0)),
                  String((inventory || []).reduce((sum: number, i: InventorySummary) => sum + i.inUse, 0)),
                  String((inventory || []).reduce((sum: number, i: InventorySummary) => sum + i.qcHold + i.repair, 0)),
                  String((inventory || []).reduce((sum: number, i: InventorySummary) => sum + i.total, 0)),
                ]}
                showTotalsInFooter
              />
            )}
          </>
        )}
      </Card>

      {/* Create Asset Modal */}
      <Modal
        open={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setNewAsset({ serialNumber: '', productSku: '', productName: '', notes: '' });
          setSelectedProductId('');
        }}
        title="Add New Asset"
        primaryAction={{
          content: 'Add Asset',
          onAction: handleCreateAsset,
          disabled: !newAsset.serialNumber || !newAsset.productSku || !newAsset.productName,
          loading: createMutation.isPending,
        }}
        secondaryActions={[
          {
            content: 'Cancel', onAction: () => {
              setShowCreateModal(false);
              setNewAsset({ serialNumber: '', productSku: '', productName: '', notes: '' });
              setSelectedProductId('');
            }
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField
              label="Serial Number"
              value={newAsset.serialNumber}
              onChange={(value) => setNewAsset((prev) => ({ ...prev, serialNumber: value }))}
              autoComplete="off"
              requiredIndicator
            />
            <Select
              label="Product"
              options={productOptions}
              value={selectedProductId}
              onChange={handleProductSelect}
              requiredIndicator
            />
            {newAsset.productSku && (
              <TextField
                label="Product SKU"
                value={newAsset.productSku}
                onChange={() => undefined}
                autoComplete="off"
                readOnly
              />
            )}
            <TextField
              label="Notes"
              value={newAsset.notes}
              onChange={(value) => setNewAsset((prev) => ({ ...prev, notes: value }))}
              multiline={2}
              autoComplete="off"
            />
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
